/**
 * Iter48 — Product Package (PDF / CAD / Revit) helpers for the Premium channel.
 * Iter60A — Extended with Floor Plan tiers (`fp_pdf`, `fp_cad`, `fp_revit`,
 *           `fp_customize`) so a single plan document can price the Premium
 *           and Floor Plan marketplaces independently.
 *
 * Business rules (Iter60A):
 *  1. Premium channel prices ONLY from `pdf | cad | revit`.
 *  2. Floor Plan channel prices ONLY from `fp_pdf | fp_cad | fp_revit`.
 *  3. `fp_customize` is an enquiry CTA — never contributes to "From" price
 *     and never enters instant-download checkout.
 *  4. Legacy `plan.price_usd` is NOT used as a fallback.
 */

export const PREMIUM_TIERS = ["pdf", "cad", "revit"];
export const FLOOR_PLAN_TIERS = ["fp_pdf", "fp_cad", "fp_revit", "fp_customize"];
// Only these floor-plan tiers contribute to "From" price / cart. Customize is
// intentionally excluded (see rule 3).
export const FLOOR_PLAN_DOWNLOAD_TIERS = ["fp_pdf", "fp_cad", "fp_revit"];
export const PACKAGE_TIERS = [...PREMIUM_TIERS, ...FLOOR_PLAN_TIERS];

export const PACKAGE_LABEL = {
  pdf:            "PDF",
  cad:            "CAD",
  revit:          "Revit",
  fp_pdf:         "Floor Plan PDF",
  fp_cad:         "Floor Plan CAD",
  fp_revit:       "Floor Plan Revit",
  fp_customize:   "Customize This Plan",
};

export const PACKAGE_TAGLINE = {
  pdf:            "PDF Plan Set",
  cad:            "CAD Package (includes PDF)",
  revit:          "Revit Package (includes CAD + PDF)",
  fp_pdf:         "Colored Floor Plan · High-resolution PDF",
  fp_cad:         "Floor Plan CAD file (.dwg)",
  fp_revit:       "Floor Plan Revit file (.rvt)",
  fp_customize:   "Request modifications · Sends an enquiry to our team",
};

/** Canonical deliverables shown as checkboxes in the admin form. Order is
 *  significant — it drives the "What's Included" list order on PDP. */
export const DELIVERABLE_OPTIONS = [
  "Floor Plans",
  "Exterior Elevations",
  "Building Sections",
  "Roof Plan",
  "Foundation Plan",
  "Site Plan",
  "Door & Window Schedule",
  "Electrical Layout",
  "Plumbing Layout",
  "HVAC Layout",
  "Ceiling Plan",
  "Structural Drawings",
  "Framing Details",
  "Beam & Column Layout",
  "Material Specifications",
  "Construction Notes",
  "3D Views",
  "High-Quality PDF",
  "Editable CAD Files",
  "Editable Revit Model",
];

/** Package-tier specific deliverables that gate visibility in "What's Included". */
const TIER_GATED = {
  "Editable CAD Files": "cad",
  "Editable Revit Model": "revit",
};

const CHANNEL_TIER_MAP = {
  premium:    PREMIUM_TIERS,
  floor_plan: FLOOR_PLAN_TIERS,
};
const CHANNEL_DOWNLOAD_TIER_MAP = {
  premium:    PREMIUM_TIERS,
  floor_plan: FLOOR_PLAN_DOWNLOAD_TIERS,
};

/** Return the tier keys valid for the given sales channel. */
export function tiersForChannel(channel, { downloadableOnly = false } = {}) {
  const map = downloadableOnly ? CHANNEL_DOWNLOAD_TIER_MAP : CHANNEL_TIER_MAP;
  return map[channel] || map.premium;
}

export const isCustomizeTier = (t) => t === "fp_customize";

/** Returns the list of enabled tiers for a plan, filtered by channel. */
export function enabledTiers(plan, channel = "premium") {
  const pkgs = plan?.packages || {};
  return tiersForChannel(channel).filter((t) => pkgs[t]?.enabled);
}

/** Enabled + downloadable (excludes customize) — used for cart eligibility. */
export function enabledDownloadTiers(plan, channel = "premium") {
  const pkgs = plan?.packages || {};
  return tiersForChannel(channel, { downloadableOnly: true }).filter((t) => pkgs[t]?.enabled);
}

/** Is this plan purchasable on the given channel? Iter60A: NO legacy fallback. */
export function isPlanForSale(plan, channel = "premium") {
  return enabledDownloadTiers(plan, channel).length > 0;
}

/** Server-authoritative for real prices; frontend mirrors to keep the
 *  "From" label and cart totals responsive. Legacy `plan.price_usd` is
 *  intentionally NOT consulted — see Iter60A pricing spec. */
export function tierPrice(plan, tier, currency = "usd") {
  const field = currency === "inr" ? "price_inr" : "price_usd";
  const pkg = plan?.packages?.[tier];
  if (!pkg?.enabled) return 0;
  return Number(pkg[field] || 0);
}

/** "From $XX" — cheapest enabled DOWNLOADABLE tier for the given channel.
 *  Prefers `plan.pricing_by_channel` (computed server-side, honours INR
 *  overrides + FX rate) when present. Falls back to a local min() only if
 *  the backend field is missing. Returns 0 when nothing is purchasable. */
export function fromPrice(plan, currency = "usd", channel = "premium") {
  const cur = currency === "inr" ? "inr" : "usd";
  const server = plan?.pricing_by_channel?.[channel];
  if (server) {
    const v = cur === "inr" ? server.from_inr : server.from_usd;
    return v == null ? 0 : Number(v);
  }
  const tiers = enabledDownloadTiers(plan, channel);
  if (!tiers.length) return 0;
  return Math.min(...tiers.map((t) => tierPrice(plan, t, cur)));
}

/** True when the plan has NO enabled purchasable tier on the given channel.
 *  UI uses this to render "Price on request" instead of "From $0". */
export function hasNoPurchasablePackage(plan, channel = "premium") {
  return fromPrice(plan, "usd", channel) <= 0;
}

/** Filter admin-selected deliverables by which tier the customer picked. */
export function deliverablesForTier(plan, tier) {
  const all = plan?.deliverables || [];
  return all.filter((d) => {
    const gated = TIER_GATED[d];
    if (!gated) return true;               // universal deliverable
    // gated deliverable shown only if its own tier ≤ selected tier
    const rank = { pdf: 0, cad: 1, revit: 2 };
    return rank[tier] >= rank[gated];
  });
}

export function includesManualDelivery(items) {
  return (items || []).some((it) => (it.package_tier || "pdf") !== "pdf");
}
